/*
|--------------------------------------------------------------------------
| Imports
|--------------------------------------------------------------------------
*/

import { Scene, PerspectiveCamera, WebGLRenderer, Object3D, Object3DEventMap, Mesh, Vector2, BufferGeometry, MeshStandardMaterial, NormalBufferAttributes, NearestFilter } from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { Ticker } from '~/Chrono/Types';
import { WorldOptions, WorldCameraOptions, WorldRendererOptions } from './Types';
import { SpriteAdd } from '~/Sprite';
import { ActorAdd } from '~/Actor';
import { TextureDataLoad } from '~/Component/Texture';
import { UpdateGetTicker, UpdateTick } from '~/Update';
import { RenderGetMaxAnisotropy } from '~/Render';
import { SceneAdd, SceneGet, SceneInit } from '~/Scene';

/*
|--------------------------------------------------------------------------
| Locals Functions
|--------------------------------------------------------------------------
*/

let _active = false;
let _canvas: HTMLCanvasElement = null;
let _scene: Scene;
let _camera: PerspectiveCamera;
let _renderer: WebGLRenderer;
let _resizeTo: Vector2 = null;
let _resizeTimeout: NodeJS.Timeout = null;
let _preUpdateFn: () => void = null;
let _updateFn: (ticker: Ticker) => void = null;
let _postUpdateFn: () => void = null;
let _frameRate = 1 / 60;
let _previousElapsed: number = 0;
let _maxAnistropy = 16;

/*
|--------------------------------------------------------------------------
| Private Functions
|--------------------------------------------------------------------------
*/

/**
 * @return {void}
 */
function _resizeDebounce(): void
{
    if (_resizeTimeout) {
        clearTimeout(_resizeTimeout);
    }

    _resizeTimeout = setTimeout((): void => {
        _resizeTimeout = null;
        _resizeTo = null;
        _renderer.setSize(_canvas.clientWidth, _canvas.clientHeight, false);
    }, 250);
}

/**
 * @return {void}
 */
function _render(): void
{
    if (_canvas.width !== _canvas.clientWidth || _canvas.height !== _canvas.clientHeight) {
        if (_resizeTo?.x !== _canvas.clientWidth || _resizeTo?.y !== _canvas.clientHeight) {
            if (_resizeTo) {
                _resizeTo.x = _canvas.clientWidth;
                _resizeTo.y = _canvas.clientHeight;
            } else {
                _resizeTo = new Vector2(_canvas.clientWidth, _canvas.clientHeight);
            }

            _resizeDebounce();
        }
    }

    _renderer.render(_scene, _camera);

    if (_active) {
        requestAnimationFrame(_render);
    }
}

/**
 * @return {void}
 */
function _update(): void
{
    UpdateTick();

    if (_preUpdateFn) {
        _preUpdateFn();
    }

    if (_updateFn) {
        _updateFn(UpdateGetTicker());
    }

    if (_postUpdateFn) {
        _postUpdateFn();
    }

    setTimeout(_update, 1000 / 60);
}

/**
 * @return {void}
 */
function _createCamera(options?: WorldCameraOptions): PerspectiveCamera
{
    return new PerspectiveCamera(
        options?.fov || 75,
        _canvas.clientWidth / _canvas.clientHeight,
        options?.near || 0.1,
        options?.far || 512,
    );
}

/**
 * @return {void}
 */
function _createRenderer(options?: WorldRendererOptions): WebGLRenderer
{
    return new WebGLRenderer({
        canvas: _canvas,
        antialias: options?.antialias || false,
    });
}

/*
|--------------------------------------------------------------------------
| Public Functions
|--------------------------------------------------------------------------
*/

/**
 * @return {Scene}
 */
export function WorldGetScene(): Scene
{
    return _scene;
}

/**
 * @return {void}
 */
export function WorldSetUpdateFunction(updateFn: (ticker: Ticker) => void): void
{
    _updateFn = updateFn;
}

/**
 * @return {void}
 */
export function WorldSetPreUpdateFunction(preUpdateFn: () => void): void
{
    _preUpdateFn = preUpdateFn;
}

/**
 * @return {void}
 */
export function WorldSetPostUpdateFunction(postUpdateFn: () => void): void
{
    _postUpdateFn = postUpdateFn;
}

/**
 * @return {void}
 */
async function _handleMeshes(parent: Object3D, forEachMeshFn: (mesh: Mesh) => void): Promise<void>
{
    for (const child of parent.children) {
        if (child.type === 'Mesh') {
            let mesh: Mesh<BufferGeometry, MeshStandardMaterial> = child as Mesh<BufferGeometry, MeshStandardMaterial>;

            if (mesh.material.map) {
                mesh.material.map.minFilter = NearestFilter;
                mesh.material.map.magFilter = NearestFilter;
                mesh.material.map.generateMipmaps = false;
            }

            if (mesh.userData.is_sprite) {
                SpriteAdd(mesh);
            } else if (mesh.userData.is_actor) {
                ActorAdd(mesh, await TextureDataLoad(`/TextureData/${mesh.userData.kind}.json`));
            } else if (!mesh.userData.is_auto_update) {
                mesh.matrixAutoUpdate = false;
            }

            if (mesh.material.map) {
                if (mesh.userData.is_max_anisotropy) {
                    mesh.material.map.anisotropy = RenderGetMaxAnisotropy();
                } else {
                    mesh.material.map.anisotropy = Math.min(mesh.userData.anisotropy ?? 0, RenderGetMaxAnisotropy());
                }
            }

            if (forEachMeshFn) {
                forEachMeshFn(child as Mesh);
            }
        } else if (child.children.length) {
            _handleMeshes(child, forEachMeshFn);
        }
    }
}

/**
 * @return {void}
 */
export async function WorldLoadGltf(gltfUri: string, forEachMeshFn?: (mesh: Mesh) => void): Promise<void>
{
    let loader = new GLTFLoader;
    let assets = await loader.loadAsync(gltfUri);

    await _handleMeshes(assets.scene, forEachMeshFn);

    SceneAdd(assets.scene);
}

/**
 * @return {[Scene, PerspectiveCamera]}
 */
export function WorldInit(options?: WorldOptions): [Scene, PerspectiveCamera, HTMLCanvasElement]
{
    _canvas = document.getElementById(options?.canvas?.id || '3d') as HTMLCanvasElement;
    _scene = SceneInit();
    _camera = _createCamera(options?.camera);
    _renderer = _createRenderer(options?.renderer);
    _maxAnistropy = _renderer.capabilities.getMaxAnisotropy();

    _renderer.setSize(_canvas.clientWidth, _canvas.clientHeight, false);

    return [_scene, _camera, _canvas];
}
